/**
 * Finding an artifact note's passage in the rendered document.
 *
 * A note stores the text the person selected plus a little of what surrounds
 * it (a W3C TextQuoteSelector), never DOM offsets: the artifact is re-rendered
 * from markdown on every version. Matching ignores whitespace, because the
 * browser's selection text and the DOM's text nodes disagree about the line
 * breaks between blocks.
 */

/** How much surrounding text a new note keeps on each side (the server allows 200). */
const CONTEXT_CHARS = 64;

export type NoteAnchor = { quote: string; prefix: string; suffix: string };

/** The document's text nodes in order, with where each starts in the joined text. */
type TextIndex = { text: string; nodes: Text[]; starts: number[] };

/**
 * Text the reader draws around the document rather than from it (the section
 * index, a passage's earlier wording under review) is marked `data-note-skip`
 * and is never part of a passage.
 */
function skipped(node: Node): boolean {
  return !!node.parentElement?.closest("[data-note-skip]");
}

function indexText(root: HTMLElement): TextIndex {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => (skipped(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  const nodes: Text[] = [];
  const starts: number[] = [];
  let text = "";
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const t = node as Text;
    nodes.push(t);
    starts.push(text.length);
    text += t.data;
  }
  return { text, nodes, starts };
}

/** Offset of a DOM boundary point in the joined text: the indexed text before it. */
function textOffset(index: TextIndex, node: Node, offset: number): number {
  if (node.nodeType === Node.TEXT_NODE) {
    const at = index.nodes.indexOf(node as Text);
    if (at >= 0) return index.starts[at] + Math.min(offset, (node as Text).data.length);
  }
  // A point between elements, or in skipped text: where the next indexed text starts.
  const point = document.createRange();
  point.setStart(node, offset);
  const next = index.nodes.findIndex((text) => point.comparePoint(text, 0) >= 0);
  return next < 0 ? index.text.length : index.starts[next];
}

/** A boundary point for a joined-text offset. */
function pointAt(index: TextIndex, offset: number): { node: Text; offset: number } | null {
  if (index.nodes.length === 0) return null;
  let lo = 0;
  let hi = index.nodes.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (index.starts[mid] <= offset) lo = mid;
    else hi = mid - 1;
  }
  const node = index.nodes[lo];
  return { node, offset: Math.min(offset - index.starts[lo], node.data.length) };
}

/** The selection as a note anchor, or null when it is empty or outside `root`. */
export function anchorFromSelection(root: HTMLElement, selection: Selection): NoteAnchor | null {
  if (selection.rangeCount === 0 || selection.isCollapsed) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  const index = indexText(root);
  const { text } = index;
  const start = textOffset(index, range.startContainer, range.startOffset);
  const end = textOffset(index, range.endContainer, range.endOffset);
  // The passage's words from the same index as its surroundings, so a
  // selection across skipped text (the index, earlier wording) still anchors.
  // The browser's own reading keeps the line breaks when it says the same.
  const indexed = text.slice(start, end).trim();
  const picked = selection.toString().trim();
  const quote = strip(picked) === strip(indexed) ? picked : indexed;
  if (!quote) return null;
  return {
    quote,
    prefix: text.slice(Math.max(0, start - CONTEXT_CHARS), start),
    suffix: text.slice(end, end + CONTEXT_CHARS),
  };
}

const isSpace = (ch: string) => /\s/.test(ch);
const strip = (s: string) => s.replace(/\s+/g, "");

/** How many characters `a` and `b` share at their ends (tail) or starts. */
function sharedEnd(a: string, b: string): number {
  let n = 0;
  while (n < a.length && n < b.length && a[a.length - 1 - n] === b[b.length - 1 - n]) n++;
  return n;
}
function sharedStart(a: string, b: string): number {
  let n = 0;
  while (n < a.length && n < b.length && a[n] === b[n]) n++;
  return n;
}

/**
 * Finds each anchor in the document: the occurrence of its quote whose
 * surroundings best match the stored prefix and suffix. Null for an anchor
 * whose text is no longer in the document.
 */
export function locateAnchors(root: HTMLElement, anchors: NoteAnchor[]): (Range | null)[] {
  const index = indexText(root);
  // The joined text with whitespace removed, and where each kept char came from.
  let compact = "";
  const from: number[] = [];
  for (let i = 0; i < index.text.length; i++) {
    if (isSpace(index.text[i])) continue;
    compact += index.text[i];
    from.push(i);
  }

  return anchors.map((anchor) => {
    const quote = strip(anchor.quote);
    if (!quote) return null;
    const prefix = strip(anchor.prefix);
    const suffix = strip(anchor.suffix);
    let best = -1;
    let bestScore = -1;
    for (let at = compact.indexOf(quote); at >= 0; at = compact.indexOf(quote, at + 1)) {
      const score =
        sharedEnd(compact.slice(Math.max(0, at - prefix.length), at), prefix) +
        sharedStart(compact.slice(at + quote.length, at + quote.length + suffix.length), suffix);
      if (score > bestScore) {
        best = at;
        bestScore = score;
      }
    }
    if (best < 0) return null;
    const start = pointAt(index, from[best]);
    const end = pointAt(index, from[best + quote.length - 1] + 1);
    if (!start || !end) return null;
    const range = document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    return range;
  });
}

/** Whether a viewport point falls on the range's text. */
export function rangeContainsPoint(range: Range, x: number, y: number): boolean {
  for (const rect of range.getClientRects()) {
    if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return true;
  }
  return false;
}

/** The CSS Custom Highlight API, where the browser has it. */
type HighlightRegistry = Map<string, unknown>;
type HighlightCtor = new (...ranges: Range[]) => unknown;

function highlightApi(): { registry: HighlightRegistry; Highlight: HighlightCtor } | null {
  const registry = (globalThis.CSS as unknown as { highlights?: HighlightRegistry } | undefined)
    ?.highlights;
  const Highlight = (globalThis as unknown as { Highlight?: HighlightCtor }).Highlight;
  return registry && Highlight ? { registry, Highlight } : null;
}

/**
 * Paints the ranges under a `::highlight(name)` style without touching the DOM
 * React owns. An empty list clears it. A browser without the API shows no
 * highlight; the notes panel still works.
 */
export function paintHighlight(name: string, ranges: Range[]): void {
  const api = highlightApi();
  if (!api) return;
  if (ranges.length === 0) api.registry.delete(name);
  else api.registry.set(name, new api.Highlight(...ranges));
}
